import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Badge } from "react-bootstrap";
import { FiPrinter, FiSend } from "react-icons/fi";
import api, { parseWIB } from "../services/api";
import bluetoothPrinter from "../utils/bluetooth";

const TransactionDetail = () => {
  const { id } = useParams();
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTransaction();
  }, [id]);

  const loadTransaction = async () => {
    try {
      const res = await api.get(`/transactions/${id}`);
      setTx(res.data);
    } catch (error) {
      toast.error("Gagal memuat transaksi");
    } finally {
      setLoading(false);
    }
  };

  // WhatsApp Web integration
  const openWAWeb = async () => {
    try {
      // Gunakan endpoint berbeda jika status selesai
      const endpoint =
        tx && tx.status === "done"
          ? `/transactions/${id}/whatsapp-completion`
          : `/transactions/${id}/whatsapp-preview`;
      const res = await api.get(endpoint);
      let phone = (res.data.phone || "").replace(/[^0-9]/g, "");
      if (phone.startsWith("0")) phone = "62" + phone.slice(1);
      if (phone.startsWith("62")) phone = phone;
      else if (phone.startsWith("8")) phone = "62" + phone;
      const message = encodeURIComponent(res.data.message || "");
      const waUrl = `https://wa.me/${phone}?text=${message}`;
      window.open(waUrl, "_blank");
    } catch (error) {
      toast.error("Gagal membuka WhatsApp Web");
    }
  };

  const [printSettings, setPrintSettings] = useState({});

  useEffect(() => {
    api
      .get("/settings")
      .then((res) => setPrintSettings(res.data || {}))
      .catch(() => {});
  }, []);

  const printReceipt = async () => {
    if (!bluetoothPrinter.isConnected()) {
      if (!bluetoothPrinter.isSupported()) {
        toast.error(
          "Web Bluetooth tidak didukung di browser ini. Gunakan Chrome/Edge.",
        );
        return;
      }
      const connect = window.confirm(
        "Printer belum terhubung. Hubungkan sekarang?",
      );
      if (!connect) return;
      const res = await bluetoothPrinter.connect();
      if (!res.success) {
        toast.error(`Gagal terhubung ke printer: ${res.error}`);
        return;
      }
    }
    try {
      const res = await bluetoothPrinter.printReceipt(tx, printSettings);
      if (res.success) {
        toast.success("Struk berhasil dicetak");
      } else {
        toast.error(`Cetak gagal: ${res.error}`);
      }
    } catch (e) {
      toast.error("Terjadi kesalahan saat mencetak");
    }
  };

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
  const fmtDt = (d) => {
    if (!d) return "-";
    const dt = parseWIB(d);
    return (
      dt.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) +
      ", " +
      dt.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
    );
  };

  if (loading)
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary"></div>
      </div>
    );
  if (!tx)
    return <div className="alert alert-danger">Transaksi tidak ditemukan</div>;

  const services = tx.details?.filter((d) => d.item_type === "service") || [];
  const products = tx.details?.filter((d) => d.item_type === "product") || [];
  const logs = tx.logs || [];
  const vehiclesLoyalty = tx.vehicles_loyalty || [];

  const statusColor = {
    waiting: "#f59e0b",
    process: "#3b82f6",
    done: "#10b981",
    cancelled: "#ef4444",
  };
  const statusLabel = {
    waiting: "Waiting",
    process: "Process",
    done: "Done",
    cancelled: "Dibatalkan",
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Link to="/transactions" className="text-decoration-none">
            &larr; Kembali
          </Link>
          <h4 className="mt-2 mb-0">{tx.invoice_number}</h4>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-light"
            disabled={tx.status !== "done"}
            onClick={async () => {
              try {
                const res = await api.get(
                  `/transactions/${id}/whatsapp-completion`,
                );
                let phone = (res.data.phone || "").replace(/[^0-9]/g, "");
                if (phone.startsWith("0")) phone = "62" + phone.slice(1);
                else if (phone.startsWith("8")) phone = "62" + phone;
                const message = encodeURIComponent(res.data.message || "");
                window.open(`https://wa.me/${phone}?text=${message}`, "_blank");
              } catch (error) {
                toast.error("Gagal membuka WhatsApp Web");
              }
            }}
          >
            <FiSend className="me-1" /> WA Selesai
          </button>
          <button
            className="btn btn-success"
            onClick={async () => {
              try {
                const res = await api.get(
                  `/transactions/${id}/whatsapp-preview`,
                );
                let phone = (res.data.phone || "").replace(/[^0-9]/g, "");
                if (phone.startsWith("0")) phone = "62" + phone.slice(1);
                else if (phone.startsWith("8")) phone = "62" + phone;
                const message = encodeURIComponent(res.data.message || "");
                window.open(`https://wa.me/${phone}?text=${message}`, "_blank");
              } catch (error) {
                toast.error("Gagal membuka WhatsApp Web");
              }
            }}
          >
            <FiSend className="me-1" /> WA Invoice
          </button>
          <button className="btn btn-primary" onClick={printReceipt}>
            <FiPrinter className="me-1" /> Cetak Struk
          </button>
        </div>
      </div>

      <div className="row g-3">
        {/* Left: items + payment */}
        <div className="col-md-8">
          {/* Detail Item & Pembayaran */}
          <div className="card p-3 mb-3">
            <h6>Detail Item &amp; Pembayaran</h6>
            {services.length > 0 && (
              <>
                <h6 className="text-muted small mt-2">Layanan</h6>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Nama</th>
                      <th>Kendaraan</th>
                      <th className="text-end">Harga</th>
                      <th className="text-end">Qty</th>
                      <th className="text-end">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((d) => {
                      const isLoyaltyFree = d.notes === "GRATIS - Redeem Loyalty";
                      const isLoyaltyUpgrade =
                        d.notes && d.notes.startsWith("Upgrade Loyalty");
                      const isLoyalty = isLoyaltyFree || isLoyaltyUpgrade;
                      return (
                        <tr key={d.id}>
                          <td>
                            {d.item_name}
                            {isLoyalty && (
                              <div>
                                <small className="text-warning fw-semibold">
                                  🎁{" "}
                                  {isLoyaltyFree
                                    ? "Redeem Loyalty (Gratis)"
                                    : "Upgrade Loyalty"}
                                </small>
                              </div>
                            )}
                          </td>
                          <td>
                            <small className="text-muted">
                              {d.vehicle_type_name
                                ? `${d.vehicle_type_name}${d.vehicle_plate ? " - " + d.vehicle_plate : ""}`
                                : "-"}
                            </small>
                          </td>
                          <td className="text-end">
                            {isLoyalty ? (
                              <>
                                <div>
                                  <s className="text-muted small">
                                    {fmt(d.unit_price)}
                                  </s>
                                </div>
                                {isLoyaltyUpgrade && (
                                  <div>
                                    <small className="text-danger">
                                      -{fmt(d.discount)}
                                    </small>
                                  </div>
                                )}
                              </>
                            ) : (
                              fmt(d.unit_price)
                            )}
                          </td>
                          <td className="text-end">{d.quantity}</td>
                          <td className="text-end fw-bold">
                            {isLoyaltyFree ? (
                              <span className="text-success">GRATIS</span>
                            ) : (
                              fmt(d.subtotal)
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
            {products.length > 0 && (
              <>
                <h6 className="text-muted small mt-2">Produk</h6>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Nama</th>
                      <th className="text-end">Harga</th>
                      <th className="text-end">Qty</th>
                      <th className="text-end">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((d) => (
                      <tr key={d.id}>
                        <td>{d.item_name}</td>
                        <td className="text-end">{fmt(d.unit_price)}</td>
                        <td className="text-end">{d.quantity}</td>
                        <td className="text-end fw-bold">{fmt(d.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Payment summary */}
            <hr className="my-2" />
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">Subtotal</td>
                  <td className="text-end">{fmt(tx.subtotal)}</td>
                </tr>
                {Number(tx.discount_amount) > 0 && (
                  <tr>
                    <td className="text-muted">Diskon</td>
                    <td className="text-end text-danger">
                      -{fmt(tx.discount_amount)}
                    </td>
                  </tr>
                )}
                <tr className="fw-bold">
                  <td>Total</td>
                  <td className="text-end text-primary">
                    {fmt(tx.total_amount)}
                  </td>
                </tr>
                {tx.payment_method === "unpaid" ? (
                  <tr>
                    <td colSpan="2" className="text-center pt-2">
                      <Badge bg="danger">BELUM DIBAYAR</Badge>
                    </td>
                  </tr>
                ) : (
                  <>
                    <tr>
                      <td className="text-muted">
                        Bayar ({tx.payment_method?.toUpperCase()})
                      </td>
                      <td className="text-end">{fmt(tx.payment_amount)}</td>
                    </tr>
                    {tx.payment_method === "cash" && (
                      <tr>
                        <td className="text-muted">Kembali</td>
                        <td className="text-end">{fmt(tx.change_amount)}</td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Loyalty Progress (multi-vehicle) */}
          {vehiclesLoyalty.length > 0 && (
            <div className="card p-3 mb-3">
              <h6>Progress Loyalty</h6>
              {vehiclesLoyalty.map((v) => (
                <div key={v.vehicle_id} className="mb-3">
                  {vehiclesLoyalty.length > 1 && (
                    <div className="small fw-semibold text-muted mb-1">
                      {v.vehicle_type} — {v.plate_number}
                    </div>
                  )}
                  {v.rules.map((lp) => {
                    const pct = Math.min(
                      100,
                      (lp.current_count / lp.required_count) * 100,
                    );
                    const canRedeem = lp.current_count >= lp.required_count;
                    return (
                      <div key={lp.rule_id} className="mb-2">
                        <div className="d-flex justify-content-between small">
                          <span>{lp.rule_name}</span>
                          <span className="fw-bold">
                            {lp.current_count}/{lp.required_count}
                          </span>
                        </div>
                        <div className="progress" style={{ height: "8px" }}>
                          <div
                            className={`progress-bar ${canRedeem ? "bg-success" : "bg-primary"}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        {canRedeem && (
                          <div className="text-end mt-1">
                            <Badge bg="success" style={{ fontSize: "0.65rem" }}>
                              BISA REDEEM — {lp.reward_service_name}
                            </Badge>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: info + process history */}
        <div className="col-md-4">
          <div className="card p-3 mb-3">
            <h6>Info Transaksi</h6>
            <table className="table table-sm table-borderless">
              <tbody>
                <tr>
                  <td className="text-muted">Status</td>
                  <td>
                    <Badge
                      bg={
                        {
                          waiting: "warning",
                          process: "primary",
                          done: "success",
                          cancelled: "danger",
                        }[tx.status] || "secondary"
                      }
                    >
                      {statusLabel[tx.status] || tx.status}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Customer</td>
                  <td>{tx.customer_name || "-"}</td>
                </tr>
                <tr>
                  <td className="text-muted">Kasir</td>
                  <td>{tx.cashier_name || "-"}</td>
                </tr>
                <tr>
                  <td className="text-muted">Tanggal</td>
                  <td>{fmtDt(tx.created_at)}</td>
                </tr>
                {tx.notes && (
                  <tr>
                    <td className="text-muted">Catatan</td>
                    <td>{tx.notes}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Process History */}
          {(logs.length > 0 || tx.created_at) && (
            <div className="card p-3">
              <h6 className="mb-3">Riwayat Proses</h6>
              <div style={{ position: "relative" }}>
                {/* vertical track line */}
                <div
                  style={{
                    position: "absolute",
                    left: 15,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: "rgba(128,128,128,0.18)",
                  }}
                />

                {/* Created / Diterima */}
                {(() => {
                  const dt = parseWIB(tx.created_at);
                  const timeStr =
                    String(dt.getHours()).padStart(2, "0") +
                    ":" +
                    String(dt.getMinutes()).padStart(2, "0");
                  const dateStr = dt.toLocaleDateString("id-ID", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  });
                  return (
                    <div
                      className="d-flex gap-3 mb-3"
                      style={{ position: "relative" }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: "#fef3c722",
                          border: "2px solid #f59e0b",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          zIndex: 1,
                        }}
                      >
                        <span style={{ fontSize: 14 }}>📋</span>
                      </div>
                      <div style={{ flex: 1, paddingTop: 4 }}>
                        <div
                          className="fw-semibold"
                          style={{ fontSize: "0.85rem", color: "#f59e0b" }}
                        >
                          Dibuat
                        </div>
                        <div
                          style={{ fontSize: "0.78rem" }}
                          className="text-muted"
                        >
                          {tx.cashier_name}
                        </div>
                        <div
                          style={{ fontSize: "0.75rem" }}
                          className="text-muted mt-1"
                        >
                          {dateStr} · {timeStr}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {logs.map((log, i) => {
                  const color = statusColor[log.to_status] || "#6c757d";
                  const label = statusLabel[log.to_status] || log.to_status;
                  const dt = new Date(log.changed_at);
                  const timeStr =
                    String(dt.getHours()).padStart(2, "0") +
                    ":" +
                    String(dt.getMinutes()).padStart(2, "0");
                  const dateStr = dt.toLocaleDateString("id-ID", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  });
                  return (
                    <div
                      key={log.id || i}
                      className="d-flex gap-3 mb-3"
                      style={{ position: "relative" }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: color + "22",
                          border: `2px solid ${color}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          zIndex: 1,
                        }}
                      >
                        <span style={{ fontSize: 14 }}>
                          {log.to_status === "done"
                            ? "✅"
                            : log.to_status === "cancelled"
                              ? "❌"
                              : log.to_status === "process"
                                ? "⚙️"
                                : "🔄"}
                        </span>
                      </div>
                      <div style={{ flex: 1, paddingTop: 4 }}>
                        <div
                          className="fw-semibold"
                          style={{ fontSize: "0.85rem", color }}
                        >
                          {label}
                        </div>
                        <div
                          style={{ fontSize: "0.78rem" }}
                          className="text-muted"
                        >
                          {log.changed_by_name || "-"}
                        </div>
                        <div
                          style={{ fontSize: "0.75rem" }}
                          className="text-muted mt-1"
                        >
                          {dateStr} · {timeStr}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetail;
